import { toast } from "sonner";

/**
 * Map the user-facing kind label to the event-bus kind taxonomy that
 * the operational chain panel, ticker, pressure panel, and pain/relief
 * receipt all listen for. Keep it loose — substring matching covers
 * variants like "Daily Report (Foreman)", "Incident Report", etc.
 */
function eventKindFor(kind) {
  const lc = String(kind || "").toLowerCase();
  if (lc.includes("toolbox")) return "toolbox-talk";
  if (lc.includes("incident") || lc.includes("near miss")) return "incident";
  if (lc.includes("daily report")) return "daily-report";
  if (lc.includes("pre-op") || lc.includes("preop") || lc.includes("equipment inspection")) return "preop-fail";
  if (lc.includes("safety meeting") || lc.includes("meeting")) return "toolbox-talk";
  if (lc.includes("qa") || lc.includes("qc")) return "qaqc";
  return null;
}

/**
 * Show a consistent auto-routing confirmation after any successful form
 * submission. Three outputs:
 *   1. Sonner toast (subtle, non-blocking).
 *   2. Fullscreen `RoutingAnimation` overlay via the `forgedops-routing`
 *      window event — cinematic chain of Form → PM Folder → Safety →
 *      Job Folder → Backup over ~2.5s.
 *   3. `forgeops:submission` window event — fires the operational
 *      ecosystem reactions: WorkflowChainPanel switches + cascades
 *      node-by-node; OperationalActivityTicker shows JUST-NOW banner
 *      and inserts the event into its rolling rail; OperationalPressure
 *      Panel increments the matching tile; RolePainReliefStrip ticks
 *      its session receipt.
 */
export function showRoutingConfirmation(kind, record) {
  const dests =
    (record && record.auto_routed_to) ||
    ["PM Folder", "Safety Folder", "Job Folder", "Admin Records", "Backup"];
  const label = kind || "Submission";
  const destStr = dests.join(" · ");

  try {
    window.dispatchEvent(
      new CustomEvent("forgedops-routing", {
        detail: { kind: label, destinations: dests },
      })
    );
  } catch { /* SSR safety */ }

  // Operational ecosystem event — drives chain panel cascade, ticker,
  // pressure tile, and pain/relief receipt across the entire app.
  const evKind = eventKindFor(label);
  if (evKind) {
    try {
      window.dispatchEvent(
        new CustomEvent("forgeops:submission", {
          detail: {
            kind: evKind,
            label,
            id: record?.id || "",
            jobNumber: record?.job_number || record?.job || "",
            attendees: record?.attendees?.length || record?.attendee_count || 0,
            at: Date.now(),
          },
        })
      );
    } catch { /* */ }
  }

  // Sales telemetry — append to demo session log (best-effort).
  try {
    import("@/lib/demoAccess").then((m) => {
      m.trackEvent?.("form_submitted", {
        kind: evKind || label,
        record_id: record?.id || null,
        destinations: dests,
      });
    });
  } catch { /* */ }

  toast.success(`${label} routed automatically`, {
    description: `→ ${destStr} · no manual email required`,
    duration: 5000,
    className: "forgedops-routing-toast",
  });
}
